import React, { useEffect } from 'react';
import { COLORS } from './colors';
import { decomposeColor } from '@mui/system';
import { Skeleton, Tooltip } from '@mui/material';
import {
  MetricCardAnchor,
  MetricCardContainer,
  MetricCardContent,
  MetricCardLabel,
  MetricCardNumber,
  MetricCardTotal,
} from './metricCard.styles';
import InfoIcon from '@mui/icons-material/Info';
import { getFormattedMetric, MetricsFormatting, MetricsIds } from '../../../components/helpers/metricsHelpers';

type MetricCardProps = {
  id: MetricsIds;
  icon: JSX.Element;
  name: string;
  tooltip?: string;
  loading: boolean;
  total: number | undefined;
  totalFormatting: MetricsFormatting;
  count?: number;
  countFormatting?: MetricsFormatting;
  isAnchor: HTMLDivElement | null;
  setSelectedMetric: React.Dispatch<React.SetStateAction<MetricsIds | undefined>>;
  selectedMetric: MetricsIds | undefined;
};

const MetricCard: React.FC<MetricCardProps> = ({
  id,
  icon,
  name,
  tooltip,
  loading,
  total,
  totalFormatting,
  count,
  countFormatting,
  isAnchor,
  setSelectedMetric,
  selectedMetric,
}) => {
  const totalFormatted = getFormattedMetric(total, totalFormatting);
  const countFormatted = count && countFormatting && getFormattedMetric(count, countFormatting);

  // selectedMetric needs to be reset after a short delay so that if the same MetricCard is clicked twice,
  // the effect on GraphCard is triggered again. Otherwise, it will only work when clicking on different MetricCards.
  useEffect(() => {
    if (selectedMetric) {
      setTimeout(() => {
        setSelectedMetric(undefined);
      }, 1000);
    }
  }, [selectedMetric, setSelectedMetric]);

  return (
    <MetricCardContainer isAnchor={Boolean(isAnchor)}>
      {isAnchor && (
        <MetricCardAnchor
          onClick={() => {
            isAnchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setSelectedMetric(id);
          }}
        />
      )}
      {icon}
      <MetricCardContent>
        {loading ? (
          <Skeleton
            variant="text"
            sx={{ fontSize: '20px', bgcolor: `rgba(${decomposeColor(COLORS.brand[40]).values},.5)` }}
            animation="wave"
          />
        ) : (
          <MetricCardNumber>
            {totalFormatted}
            {count && <MetricCardTotal> ({countFormatted})</MetricCardTotal>}
          </MetricCardNumber>
        )}
        <MetricCardLabel>
          {name}
          {tooltip && (
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: tooltip }} />}>
              <InfoIcon style={{ height: 16, width: 16, marginLeft: 4 }} />
            </Tooltip>
          )}
        </MetricCardLabel>
      </MetricCardContent>
    </MetricCardContainer>
  );
};

export default MetricCard;
